import { Fragment, type ReactNode } from "react";
import { ExternalLink, Package } from "lucide-react";
import type { Product, ProductOffer, ProductVariant } from "@sakura-tech-lab/dashboard-api";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatPrice, humanizeKey, pickLocalized } from "./product-detail-utils";

interface ProductOffersProps {
  readonly product: Product;
  readonly language: string;
  readonly variantLabels: ReadonlyMap<string, string>;
}

interface OfferGroup {
  readonly variant: ProductVariant | null;
  readonly variantLabel: string;
  readonly offers: readonly (ProductOffer | null)[];
}

export function ProductOffers({ product, language, variantLabels }: ProductOffersProps) {
  const groups = buildOfferGroups(product, variantLabels);
  const offerCount = groups.reduce(
    (count, group) => count + group.offers.filter(Boolean).length,
    0,
  );
  if (groups.length === 0) return null;
  const grouped = groups.length > 1;

  return (
    <Card className="min-w-0">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <Package aria-hidden="true" className="h-4 w-4" />
              </span>
              Offers
            </CardTitle>
            <CardDescription>
              Availability and source listings across every product variant.
            </CardDescription>
          </div>
          <Badge variant="outline">
            {offerCount} {offerCount === 1 ? "offer" : "offers"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
        <Table className="block table-fixed xl:table">
          <TableHeader className="hidden xl:table-header-group">
            <TableRow>
              <TableHead className="w-[32%]">Seller &amp; reference</TableHead>
              <TableHead className="w-[18%]">Price</TableHead>
              <TableHead className="w-[27%]">Availability</TableHead>
              <TableHead className="w-[23%]">Condition</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="block space-y-3 xl:table-row-group xl:space-y-0">
            {groups.map((group) => (
              <Fragment key={group.variant?.id ?? "unassigned"}>
                {grouped ? (
                  <TableRow className="block bg-muted/30 hover:bg-muted/30 xl:table-row">
                    <TableCell
                      colSpan={4}
                      className="block px-4 py-2 text-xs font-semibold text-muted-foreground xl:table-cell"
                    >
                      {group.variantLabel}
                    </TableCell>
                  </TableRow>
                ) : null}
                {group.offers.map((offer, index) => (
                  <OfferTableRow
                    key={offer?.id ?? `empty-${group.variant?.id ?? "unassigned"}-${index}`}
                    offer={offer}
                    language={language}
                  />
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function OfferTableRow({
  offer,
  language,
}: {
  readonly offer: ProductOffer | null;
  readonly language: string;
}) {
  const regularPrice = formatPrice(offer?.price);
  const discountedPrice = formatPrice(offer?.priceDiscount);
  const currentPrice = discountedPrice ?? regularPrice;
  const conditionNotes = pickLocalized(offer?.conditionNotes, language);

  return (
    <TableRow className="block overflow-hidden rounded-lg border bg-card hover:bg-muted/20 xl:table-row xl:rounded-none xl:border-x-0 xl:border-t-0">
      <ResponsiveCell label="Seller & reference">
        {offer ? (
          <div className="min-w-0 space-y-1.5">
            {offer.vendor || offer.vendorId ? (
              <p className="break-words font-medium text-foreground">
                {offer.vendor || offer.vendorId}
              </p>
            ) : null}
            {offer.sku ? (
              <p className="break-all font-mono text-xs text-muted-foreground">SKU {offer.sku}</p>
            ) : null}
            {offer.externalRef ? (
              offer.url ? (
                <a
                  href={offer.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-full items-start gap-1 text-xs text-primary underline-offset-4 hover:underline"
                >
                  <span className="break-all">{offer.externalRef}</span>
                  <ExternalLink aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0" />
                </a>
              ) : (
                <p className="break-all text-xs text-muted-foreground">{offer.externalRef}</p>
              )
            ) : offer.url ? (
              <a
                href={offer.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
              >
                Open listing
                <ExternalLink aria-hidden="true" className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No offers for this variant</p>
        )}
      </ResponsiveCell>

      <ResponsiveCell label="Price">
        {currentPrice ? (
          <div className="space-y-0.5">
            <p className="font-semibold text-foreground">{currentPrice}</p>
            {discountedPrice && regularPrice && regularPrice !== discountedPrice ? (
              <p className="text-xs text-muted-foreground line-through">{regularPrice}</p>
            ) : null}
          </div>
        ) : (
          <EmptyValue />
        )}
      </ResponsiveCell>

      <ResponsiveCell label="Availability">
        {offer ? (
          <div className="flex flex-wrap gap-1.5">
            <StateBadge
              active={offer.inStock}
              activeLabel="In stock"
              inactiveLabel="Out of stock"
              negativeWhenInactive
            />
            <StateBadge
              active={offer.isPublished}
              activeLabel="Published"
              inactiveLabel="Unpublished"
            />
            <StateBadge
              active={!offer.unpublishedByModeration}
              activeLabel="Not suppressed"
              inactiveLabel="Moderation suppressed"
              negativeWhenInactive
            />
          </div>
        ) : (
          <EmptyValue />
        )}
      </ResponsiveCell>

      <ResponsiveCell label="Condition">
        {offer?.condition || conditionNotes ? (
          <div className="space-y-1">
            {offer?.condition ? (
              <p className="font-medium text-foreground">{humanizeKey(offer.condition)}</p>
            ) : null}
            {conditionNotes ? (
              <p className="break-words text-xs leading-relaxed text-muted-foreground">
                {conditionNotes}
              </p>
            ) : null}
          </div>
        ) : (
          <EmptyValue />
        )}
      </ResponsiveCell>
    </TableRow>
  );
}

function ResponsiveCell({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <TableCell className="block min-w-0 border-b px-4 py-3 last:border-b-0 xl:table-cell xl:border-b-0 xl:align-top">
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground xl:hidden">
        {label}
      </p>
      {children}
    </TableCell>
  );
}

function StateBadge({
  active,
  activeLabel,
  inactiveLabel,
  negativeWhenInactive = false,
}: {
  readonly active: boolean;
  readonly activeLabel: string;
  readonly inactiveLabel: string;
  readonly negativeWhenInactive?: boolean;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
          : negativeWhenInactive
            ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
      )}
    >
      {active ? activeLabel : inactiveLabel}
    </Badge>
  );
}

function EmptyValue() {
  return <span className="text-sm text-muted-foreground" aria-label="Not provided">—</span>;
}

function buildOfferGroups(
  product: Product,
  variantLabels: ReadonlyMap<string, string>,
): OfferGroup[] {
  const variants = product.variants ?? [];
  const variantById = new Map(variants.map((variant) => [variant.id, variant]));
  const offersByVariant = new Map<string, ProductOffer[]>();
  const unassignedOffers: ProductOffer[] = [];
  const seenOfferIds = new Set<string>();

  const addOffer = (offer: ProductOffer, fallbackVariantId?: string) => {
    if (seenOfferIds.has(offer.id)) return;
    seenOfferIds.add(offer.id);
    const variantId = offer.variantId ?? fallbackVariantId;
    if (variantId && variantById.has(variantId)) {
      const offers = offersByVariant.get(variantId) ?? [];
      offers.push(offer);
      offersByVariant.set(variantId, offers);
    } else {
      unassignedOffers.push(offer);
    }
  };

  for (const variant of variants) {
    for (const offer of variant.offers) addOffer(offer, variant.id);
  }
  for (const offer of product.offers ?? []) addOffer(offer);

  const groups: OfferGroup[] = variants.map((variant, index) => {
    const offers = offersByVariant.get(variant.id) ?? [];
    return {
      variant,
      variantLabel: variantLabels.get(variant.id) ?? `Variant ${index + 1}`,
      offers: offers.length > 0 ? offers : [null],
    };
  });
  if (unassignedOffers.length > 0) {
    groups.push({
      variant: null,
      variantLabel: variants.length > 0 ? "Unassigned" : "Default",
      offers: unassignedOffers,
    });
  }

  return groups;
}
